import {
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Injector,
  ValueProvider,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {LoadingController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of} from 'rxjs';
import {finalize} from 'rxjs/operators';

import {getTranslation} from 'shared/helpers/get-translation';

import {Daytime, DaytimeService, QuestionTime} from 'shared/daytime';
import {NavService} from 'shared/nav/nav.service';
import {ButtonListPage} from '../../components/button-list-page/button-list-page';
import {EnergylevelPage} from '../../components/energylevel-page/energylevel-page';
import {MessagePage} from '../../components/message-page/message-page';
import {MultiSliderPage} from '../../components/multi-slider-page/multi-slider-page';
import {PerformancelevelPage} from '../../components/performancelevel-page/performancelevel-page';
import {QuestionPage} from '../../components/question-page/question-page';
import {RatingPage} from '../../components/rating-page/rating-page';
import {SliderPage} from '../../components/slider-page/slider-page';
import {YesNoPage} from '../../components/yes-no-page/yes-no-page';
import {AnswerType, Question, TipList} from '../../model/model';
import {TipService} from '../../services/tip.service';

const COMPONENT_MAPPING: Dictionary<Newable<QuestionPage>> = {
  buttonList: ButtonListPage,
  message: MessagePage,
  energylevel: EnergylevelPage,
  yesno: YesNoPage,
  rating: RatingPage,
  slider: SliderPage,
  multislider: MultiSliderPage,
  performancelevel: PerformancelevelPage,
};

@Component({
  selector: 'tip-page',
  templateUrl: 'tip-page.html',
  styleUrls: ['tip-page.scss'],
  // entryComponents: [
  //   ButtonListPage,
  //   MessagePage,
  //   EnergylevelPage,
  //   YesNoPage,
  //   RatingPage,
  //   SliderPage,
  //   MultiSliderPage,
  //   PerformancelevelPage,
  // ],
})
export class TipPage {

  @ViewChild('dynamicContainer', { read: ViewContainerRef, static: true }) private dynamicContainer: ViewContainerRef;

  private title: string;
  private description: string;
  private question: Question;
  private questionTime: QuestionTime;
  private currentQuestionTime: QuestionTime;
  private tipList: TipList;
  private isRating: boolean;
  private overrideTitle: string;
  private isInReflectionMode: boolean;
  private subtree: string;
  private newButtons: Array<boolean>;

  private get isQuestion(): boolean {
    return typeof this.question !== 'undefined';
  }

  constructor(
    public navService: NavService,
    private tipService: TipService,
    private translateService: TranslateService,
    private daytimeService: DaytimeService,
    private resolver: ComponentFactoryResolver,
    private loadingCtrl: LoadingController,
  ) {
    this.question = this.navService.get('question');
    this.questionTime = this.navService.get('questionTime');
    this.currentQuestionTime = this.navService.get('currentQuestionTime');
    this.overrideTitle = this.navService.get('overrideTitle');
    this.isInReflectionMode = this.navService.get('isInReflectionMode');
    this.subtree = this.navService.get('subtree');
    this.newButtons = this.navService.get('newButtons');

    if (!this.isQuestion) {
      this.tipList = this.navService.get('tipList');
      this.isRating = this.navService.get('isRating');
    }
  }

  private ngAfterViewInit(): void { // TODO MIG war ngAfterViewInit
    if (this.isQuestion) {
      this.buildQuestionPage();
      return;
    }
    this.buildTipPage();
  }

  private buildQuestionPage(): void {
    const lang: string = this.translateService.currentLang;

    this.title = this.overrideTitle || getTranslation(this.question.title, this.translateService);
    this.description = getTranslation(this.question.description, this.translateService);
    this.resolveQuestionComponent();
  }

  private buildTipPage(): void {
    this.translateService.get([
      'TIPS.TITLE',
      'TIPS.HELPFUL_PROMPT',
      'TIPS.RATING_PROMPT',
    ]).subscribe((translations) => {
      this.title = this.overrideTitle || translations['TIPS.TITLE'];
      const helpfulPrompt: string = translations['TIPS.HELPFUL_PROMPT'];
      const ratingPrompt: string = translations['TIPS.RATING_PROMPT'];

      this.description = getTranslation(this.tipList[0].text, this.translateService);

      if (this.isRating) {
        this.description = `${ratingPrompt}\n\n${this.description}`;
      } else if (this.tipList.length > 1) {
        this.description = `${this.description}\n\n${helpfulPrompt}`;
      }
      this.resolveTipComponent();
    });
  }

  private resolveQuestionComponent(): void {
    const questionDate: Date = this.questionTime !== undefined
      ? this.questionTime.date
      : null;
    const currentQuestionDate: Date = this.currentQuestionTime !== undefined
      ? this.currentQuestionTime.date
      : null;

    const inputProviders: Array<ValueProvider> = [
      { provide: 'question', useValue: this.question },
      { provide: 'title', useValue: this.title },
      { provide: 'description', useValue: this.description },
      { provide: 'questionDate', useValue: questionDate },
      { provide: 'currentQuestionDate', useValue: currentQuestionDate },
      { provide: 'skipButtonsVisible', useValue: this.isInReflectionMode },
      { provide: 'asGoal', useValue: false },
      { provide: 'newButtons', useValue: this.newButtons || []},
    ];
    const injector: Injector = Injector.create({providers: inputProviders});

    //const injector: ReflectiveInjector = ReflectiveInjector.fromResolvedProviders(resolvedInputs, this.dynamicContainer.parentInjector);

    const factory: ComponentFactory<QuestionPage> = this.resolver.resolveComponentFactory(COMPONENT_MAPPING[this.question.type.name]);
    const component: ComponentRef<QuestionPage> = this.dynamicContainer.createComponent(factory, 0, injector);

    component.instance.answerSelected.subscribe(this.onAnswerSelected.bind(this));
    component.instance.questionSkipped.subscribe(this.onQuestionSkipped.bind(this));
    component.instance.allQuestionsSkipped.subscribe(this.onAllQuestionsSkipped.bind(this));
  }

  private resolveTipComponent(): void {
    const questionDate: Date = this.questionTime
      ? this.questionTime.date
      : null;
    const currentQuestionDate: Date = this.currentQuestionTime
      ? this.currentQuestionTime.date
      : null;

    const inputProviders: Array<ValueProvider> = [
      { provide: 'question', useValue: null },
      { provide: 'title', useValue: this.overrideTitle || this.title },
      { provide: 'description', useValue: this.description },
      { provide: 'questionDate', useValue: questionDate },
      { provide: 'currentQuestionDate', useValue: currentQuestionDate },
      { provide: 'skipButtonsVisible', useValue: this.isInReflectionMode },
      { provide: 'asGoal', useValue: this.isRating && this.tipList[0].goal},
      { provide: 'newButtons', useValue: this.newButtons || []},
    ];
    // const resolvedInputs: Array<ResolvedReflectiveProvider> = ReflectiveInjector.resolve(inputProviders);
    // const injector: ReflectiveInjector = ReflectiveInjector.fromResolvedProviders(resolvedInputs, this.dynamicContainer.parentInjector);
    const injector: Injector = Injector.create({providers: inputProviders});

    let pageClass: Newable<QuestionPage>;
    if (this.isRating) {
      pageClass = RatingPage;
    } else {
      pageClass = this.tipList.length > 1 ? YesNoPage : MessagePage;
    }

    const factory: ComponentFactory<QuestionPage> = this.resolver.resolveComponentFactory(pageClass);
    const component: ComponentRef<QuestionPage> = this.dynamicContainer.createComponent(factory, 0, injector);

    component.instance.answerSelected.subscribe(this.onAnswerSelected.bind(this));
    component.instance.questionSkipped.subscribe(this.onQuestionSkipped.bind(this));
    component.instance.allQuestionsSkipped.subscribe(this.onAllQuestionsSkipped.bind(this));
    component.instance.answerAsGoal.subscribe(this.onAnswerAsGoal.bind(this));
  }

  private onQuestionSkipped(): void {
    this.postQuestionSkipped();
  }

  private onAllQuestionsSkipped(): void {
    this.postAllQuestionsSkipped();
  }

  private async postAllQuestionsSkipped(): Promise<void> {
    const loader: HTMLIonLoadingElement = await this.loadingCtrl.create({});
    loader.present().then(() => {
      return this.tipService.skipOldReflections()
        .pipe(finalize(() => { loader.dismiss(); }))
        .subscribe(() => {
          this._navigateToCurrentReflection();
          return;
        });
    });
  }

  private onAnswerSelected(value: AnswerType): void {
    if (this.newButtons) {
      this.newButtons[<number> value] = false;
    }
    this.doAnswerSelected(value, false);
  }

  private onAnswerAsGoal(value: AnswerType): void {
    this.doAnswerSelected(value, true);
  }

  private doAnswerSelected(value: AnswerType, asGoal: boolean): void {
    if (this.isQuestion) {
      this.postQuestionAnswer(value);
      return;
    }

    if (this.isRating) {
      this.postTipRating(value, asGoal);
      return;
    }

    if (this.tipList.length > 1 && value === 'no') {
      const [head, ...otherTips]: TipList = this.tipList;
      this.navService.push('tips/' + (new Date()).getTime(), {
        tipList: otherTips,
        questionTime: this.questionTime,
        currentQuestionTime: this.currentQuestionTime,
        isInReflectionMode: this.isInReflectionMode,
        subtree: this.subtree,
      });
      return;
    }

    this.navService.push('tips/' + (new Date()).getTime(), {
      tipList: this.tipList,
      isRating: true,
      questionTime: this.questionTime,
      currentQuestionTime: this.currentQuestionTime,
      isInReflectionMode: this.isInReflectionMode,
      subtree: this.subtree,
    });
  }

  private async postQuestionSkipped(): Promise<void> {
    const loader: HTMLIonLoadingElement = await this.loadingCtrl.create({});
    loader.present().then(() => {
      this.tipService.skipReflection(this.question._id, this.questionTime.date)
        .pipe(finalize(() => { loader.dismiss(); }))
        .subscribe((questionOrTips) => {
          this._navigateToNextReflection();
          return;
        });
    });
  }

  private async postQuestionAnswer(value: AnswerType) {
    const loader: HTMLIonLoadingElement = await this.loadingCtrl.create({});
    loader.present().then(() => {
      const questionAnswerDate: Date = this.questionTime
        ? this.questionTime.date
        : null;
      this.tipService.postAnswer(this.question._id, value, questionAnswerDate, this.subtree)
        .pipe(finalize(() => { loader.dismiss(); }))
        .subscribe((questionOrTips) => {
          if (!questionOrTips) {
            this.navService.root('dashboard');
            return;
          }

          const isTipList: boolean = questionOrTips instanceof Array;
          if (!isTipList) {
            const question: Question = <Question> questionOrTips;
            this.navService.push('tips/' + question._id, {
              question: question,
              overrideTitle: this.overrideTitle,
              questionTime: this.questionTime,
              currentQuestionTime: this.currentQuestionTime,
              isInReflectionMode: this.isInReflectionMode,
              subtree: this.subtree,
            } );
            return;
          }

          const lang: string = this.translateService.currentLang;
          let tipList: TipList = <TipList> questionOrTips;
          tipList = tipList.filter((tip) => {
            const hasText: boolean = tip.text[lang] && tip.text[lang].length > 0;
            return hasText;
          });

          if (tipList.length <= 0) {
            this._navigateToNextReflection();
            return;
          }

          this.navService.push('tips/' + (new Date()).getTime(), {
            tipList: tipList,
            overrideTitle: this.overrideTitle,
            questionTime: this.questionTime,
            currentQuestionTime: this.currentQuestionTime,
            isInReflectionMode: this.isInReflectionMode,
            subtree: this.subtree,
          });
        });
    });
  }

  private _navigateToCurrentReflection(): void {
    this.daytimeService.getCurrentQuestionTime()
      .subscribe((currentQuestionTime) => {
        this._navigateToReflection(currentQuestionTime);
      });
  }

  private _getQuestionByQuestionTime(questionTime: QuestionTime): Observable<Question> {
    const hasNoQuestion: boolean = questionTime === null;

    if (hasNoQuestion) {
      return of(null);
    }

    switch (questionTime.daytime) {
      case Daytime.Morning:
      case Daytime.Midday: return this.tipService.getMorningInteration();
      case Daytime.Evening: return this.tipService.getEveningInteration();
      case Daytime.Resilience: return this.tipService.getResilienceInteration();
      case Daytime.Night: return this.tipService.getNightInteration();
      default: return this.tipService.getMorningInteration();
    }
  }

  private _getTitleByQuestionTime(questionTime: QuestionTime): string {

    switch (questionTime.daytime) {
      case Daytime.Morning:
      case Daytime.Midday: return 'REFLECTION.MORNING';
      case Daytime.Evening: return 'REFLECTION.EVENING';
      case Daytime.Resilience: return 'REFLECTION.RESILIENCE';
      case Daytime.Night: return 'REFLECTION.NIGHT';
      default: return 'REFLECTION.MORNING';
    }
  }

  private _navigateToReflection(questionTime: QuestionTime): void {

    const hasNoQuestionTime: boolean = questionTime === null;

    if (hasNoQuestionTime) {
      this.navService.root('dashboard');
      return;
    }

    const title: string = this._getTitleByQuestionTime(questionTime);
    let hasNextQuestion: boolean;

    this._getQuestionByQuestionTime(questionTime)
      .subscribe((question) => {

        hasNextQuestion = question !== null;

        if (hasNextQuestion) {
          this.translateService.get(title).subscribe((translatedTitle) => {
            this.navService.root('tips/' + question._id, {
              question: question,
              overrideTitle: translatedTitle,
              questionTime: questionTime,
              currentQuestionTime: this.currentQuestionTime,
              isInReflectionMode: this.isInReflectionMode,
              subtree: this.subtree,
            });
          });
        } else {

          this.navService.root('dashboard');
        }
      });
  }

  private _navigateToNextReflection(): void {
    this.daytimeService.getNextQuestionTime(this.questionTime)
      .subscribe((nextQuestionTime) => {
        this._navigateToReflection(nextQuestionTime);
      });
  }

  private async postTipRating(value: AnswerType, asGoal: boolean) {
    const loader: HTMLIonLoadingElement = await this.loadingCtrl.create({});
    loader.present().then(() => {
      const id: string = this.tipList[0]._id;
      this.tipService.postTipRating(id, value)
        .pipe(finalize(() => { loader.dismiss(); }))
        .subscribe((success) => {
          if ( asGoal ) {
            this.navService.root('goals/edit', {tip: this.tipList[0]})
          } else {
            this._navigateToNextReflection();
          }
        });
    });
  }

}
